<script>
import TicketCard from './TicketCard.vue';

/**
 * Presentational Kanban column. Closed column hosts closed + merged cards;
 * archive visibility is controlled by parent via archivedTickets / showArchived.
 */
export default {
    name: 'TicketColumn',
    components: { TicketCard },
    props: {
        columnKey: { type: String, required: true },
        label: { type: String, required: true },
        tickets: { type: Array, default: () => [] },
        archivedTickets: { type: Array, default: () => [] },
        archivedCount: { type: Number, default: 0 },
        pagination: { type: Object, default: () => ({}) },
        showArchived: { type: Boolean, default: false },
        showAssignment: { type: Boolean, default: false },
        highlightedId: { type: [Number, String], default: null },
        assignees: { type: Array, default: () => [] },
        currentUserId: { type: [Number, String], default: null },
    },
    emits: [
        'assign',
        'archive',
        'archive-all',
        'restore',
        'page',
        'update:showArchived',
        'open',
    ],
    computed: {
        isClosed() {
            return this.columnKey === 'closed';
        },
        columnClass() {
            return `tickets-index-column--${this.columnKey}`;
        },
        displayedTickets() {
            if (this.isClosed && this.showArchived) {
                return [...this.tickets, ...this.archivedTickets];
            }
            return this.tickets;
        },
        archivedIdSet() {
            return new Set((this.archivedTickets || []).map((ticket) => String(ticket.id)));
        },
        currentPage() {
            return Number(this.pagination.current_page || 1);
        },
        lastPage() {
            return Number(this.pagination.last_page || 1);
        },
        totalTickets() {
            return Number(this.pagination.total ?? this.tickets.length);
        },
    },
    methods: {
        isHighlighted(ticket) {
            if (this.highlightedId === null || this.highlightedId === undefined) {
                return false;
            }
            return String(ticket.id) === String(this.highlightedId);
        },
        isTicketArchived(ticket) {
            if (ticket.archived_at) {
                return true;
            }
            return this.archivedIdSet.has(String(ticket.id));
        },
        onShowArchivedChange(event) {
            this.$emit('update:showArchived', event.target.checked);
        },
        onArchiveAll() {
            const ids = this.tickets.map((ticket) => ticket.id);
            this.$emit('archive-all', ids);
        },
        changePage(page) {
            if (page < 1 || page > this.lastPage || page === this.currentPage) {
                return;
            }
            this.$emit('page', { columnKey: this.columnKey, page });
        },
    },
};
</script>

<template>
    <section
        class="tickets-index-column"
        :class="columnClass"
        :data-column="columnKey"
    >
        <header class="tickets-index-column__header">
            <div class="tickets-index-column__header-inner">
                <div class="tickets-index-column__title-group">
                    <h2 class="tickets-index-column__title">{{ label }}</h2>
                    <span class="tickets-index-column__count">{{ totalTickets }}</span>
                </div>
                <div
                    v-if="isClosed"
                    class="tickets-index-column__archive-controls"
                >
                    <span class="tickets-index-column__archived-count">
                        Archiviati: {{ archivedCount }}
                    </span>
                    <label class="tickets-index-column__show-archived">
                        <input
                            type="checkbox"
                            :checked="showArchived"
                            aria-label="Mostra archiviati"
                            @change="onShowArchivedChange"
                        />
                        <span>Mostra archiviati</span>
                    </label>
                    <button
                        v-if="tickets.length > 0"
                        type="button"
                        class="tickets-index-column__archive-all"
                        @click="onArchiveAll"
                    >
                        <span class="tickets-index-column__archive-all-icon" aria-hidden="true">▣</span>
                        Archivia tutti
                    </button>
                </div>
            </div>
        </header>

        <div class="tickets-index-column__body">
            <div
                v-if="displayedTickets.length"
                class="tickets-index-column__cards"
            >
                <TicketCard
                    v-for="ticket in displayedTickets"
                    :key="ticket.id"
                    :ticket="ticket"
                    :archived="isTicketArchived(ticket)"
                    :show-assignment="showAssignment"
                    :highlighted="isHighlighted(ticket)"
                    :assignees="assignees"
                    :current-user-id="currentUserId"
                    @assign="$emit('assign', $event)"
                    @archive="$emit('archive', $event)"
                    @restore="$emit('restore', $event)"
                    @open="$emit('open', $event)"
                />
            </div>
            <div
                v-else
                class="tickets-index-column__empty"
            >
                <span class="tickets-index-column__empty-icon" aria-hidden="true">◻</span>
                <p>Nessun ticket</p>
            </div>
            <nav
                v-if="lastPage > 1"
                class="tickets-index-column__pagination"
                :aria-label="'Paginazione colonna ' + label"
            >
                <button
                    type="button"
                    class="tickets-index-btn tickets-index-btn--ghost tickets-index-btn--sm"
                    :disabled="currentPage <= 1"
                    @click="changePage(currentPage - 1)"
                >
                    ‹ Precedenti
                </button>
                <span>Pagina {{ currentPage }} di {{ lastPage }}</span>
                <button
                    type="button"
                    class="tickets-index-btn tickets-index-btn--ghost tickets-index-btn--sm"
                    :disabled="currentPage >= lastPage"
                    @click="changePage(currentPage + 1)"
                >
                    Successivi ›
                </button>
            </nav>
        </div>
    </section>
</template>
